import { useState, useEffect } from "react";
import { Modal, ModalHeader, ModalBody } from "reactstrap";
import { callApi } from "../../../ApiHelper/ApiHelper";
import { apiUrl } from "../../../ApiHelper/ApiUrl";
import { toast } from "react-toastify";

const FIELD_STYLE = {
    width: "100%",
    border: "1px solid #d1d5db",
    borderRadius: 8,
    padding: "10px 14px",
    fontSize: "0.95rem",
    color: "#1e293b",
    outline: "none",
    background: "#fff",
    fontFamily: "inherit",
};

const LABEL_STYLE = {
    display: "block",
    fontSize: "1rem",
    fontWeight: 700,
    letterSpacing: "0.07em",
    textTransform: "uppercase",
    color: "#64748b",
    marginBottom: 6,
};

export default function AddServiceModal({ modal, toggle, machineryId, machineryName, assetNo, onSuccess }) {
    const [isLoading, setIsLoading] = useState(false);
    const [vendors, setVendors] = useState([]);
    const [form, setForm] = useState({
        date: new Date().toISOString().split("T")[0],
        workDone: "",
        vendorId: "",
        technicianName: "",
        atHours: "",
        cost: "",
        poNumber: "",
        ptNumber: "",
    });

    useEffect(() => {
        if (modal) {
            GetVendorList();
        }
    }, [modal]);

    const GetVendorList = () => {
        callApi(apiUrl.get_vendor, { method: "GET" }).then((res) => {
            if (res?.status === true) {
                const formatted = res.data?.vendor?.map((v) => ({
                    id: v.id,
                    name: v.firmName,
                })) || [];
                setVendors(formatted);
            }
        });
    };

    const reset = () => {
        setForm({
            date: new Date().toISOString().split("T")[0],
            workDone: "",
            vendorId: "",
            technicianName: "",
            atHours: "",
            cost: "",
            poNumber: "",
            ptNumber: "",
        });
    };

    const handleClose = () => {
        reset();
        toggle("service");
    };

    const handleChange = (e) => {
        setForm((p) => ({ ...p, [e.target.name]: e.target.value }));
    };

    const handleSubmit = () => {
        if (!form.date) return toast.error("Please select service date");
        if (!form.workDone) return toast.error("Please enter work done description");
        if (!form.atHours) return toast.error("Please enter hours / km reading");

        const payload = {
            machineryId,
            date: form.date,
            workDone: form.workDone,
            vendorId: form.vendorId || null,
            technicianName: form.technicianName || null,
            atHours: Number(form.atHours),
            cost: form.cost ? Number(form.cost) : 0,
            poNumber: form.poNumber || null,
            ptNumber: form.ptNumber || null,
        };

        setIsLoading(true);
        callApi(apiUrl.add_machinery_service, { method: "POST", data: payload }).then((res) => {
            setIsLoading(false);
            if (res?.status === true) {
                toast.success(res?.message || "Service logged successfully");
                reset();
                toggle("service");
                onSuccess?.();
            } else {
                toast.error(res?.message || "Failed to log service");
            }
        });
    };

    return (
        <Modal isOpen={modal} toggle={handleClose} centered size="lg">
            <div style={{ borderRadius: 12, overflow: "hidden" }}>
                {/* Header */}
                <div style={{ padding: "24px 28px 16px", borderBottom: "1px solid #f1f5f9" }}>
                    <div className="d-flex align-items-start justify-content-between">
                        <div>
                            <h5 style={{ margin: 0, fontWeight: 800, fontSize: "1.2rem", color: "#0f172a" }}>
                                Log service / maintenance
                            </h5>
                            {(machineryName || assetNo) && (
                                <p style={{ margin: "4px 0 0", fontSize: "0.88rem", color: "#64748b" }}>
                                    {[machineryName, assetNo].filter(Boolean).join(" · ")}
                                </p>
                            )}
                        </div>
                        <button
                            onClick={handleClose}
                            style={{ background: "none", border: "none", fontSize: "1.3rem", color: "#94a3b8", cursor: "pointer", lineHeight: 1, padding: 0 }}
                        >
                            ×
                        </button>
                    </div>
                </div>

                {/* Body */}
                <div style={{ padding: "24px 28px", maxHeight: "70vh", overflowY: "auto" }}>
                    {/* Row 1: Date & Hours/KM */}
                    <div className="row g-3 mb-3">
                        <div className="col-6">
                            <label style={LABEL_STYLE}>Service Date</label>
                            <input
                                type="date"
                                name="date"
                                value={form.date}
                                onChange={handleChange}
                                style={FIELD_STYLE}
                            />
                        </div>
                        <div className="col-6">
                            <label style={LABEL_STYLE}>Reading (Hours / KM)</label>
                            <input
                                type="number"
                                name="atHours"
                                value={form.atHours}
                                onChange={handleChange}
                                placeholder="e.g. 3920"
                                style={FIELD_STYLE}
                            />
                        </div>
                    </div>

                    {/* Row 2: Work Done description */}
                    <div className="mb-3">
                        <label style={LABEL_STYLE}>Work Done / Description</label>
                        <textarea
                            name="workDone"
                            value={form.workDone}
                            onChange={handleChange}
                            placeholder="Describe hydraulic oil change, filters, alignment, etc."
                            rows={3}
                            style={{ ...FIELD_STYLE, resize: "none" }}
                        />
                    </div>

                    {/* Row 3: Vendor selection (registered SCM vendor) */}
                    <div className="row g-3 mb-3">
                        <div className="col-6">
                            <label style={LABEL_STYLE}>Vendor</label>
                            <select
                                name="vendorId"
                                value={form.vendorId}
                                onChange={handleChange}
                                style={{ ...FIELD_STYLE, cursor: "pointer" }}
                            >
                                <option value="">Select registered vendor…</option>
                                {vendors.map((v) => (
                                    <option key={v.id} value={v.id}>{v.name}</option>
                                ))}
                            </select>
                        </div>
                        <div className="col-6">
                            <label style={LABEL_STYLE}>Technician / Workshop Name</label>
                            <input
                                type="text"
                                name="technicianName"
                                value={form.technicianName}
                                onChange={handleChange}
                                placeholder="e.g. Ramesh Auto Works"
                                style={FIELD_STYLE}
                            />
                        </div>
                    </div>

                    {/* Row 4: Cost & PO Number */}
                    <div className="row g-3 mb-3">
                        <div className="col-4">
                            <label style={LABEL_STYLE}>Total Cost (₹)</label>
                            <input
                                type="number"
                                name="cost"
                                value={form.cost}
                                onChange={handleChange}
                                placeholder="e.g. 18400"
                                style={FIELD_STYLE}
                            />
                        </div>
                        <div className="col-4">
                            <label style={LABEL_STYLE}>PO Number</label>
                            <input
                                type="text"
                                name="poNumber"
                                value={form.poNumber}
                                onChange={handleChange}
                                placeholder="PO/25-26/0121"
                                style={FIELD_STYLE}
                            />
                        </div>
                        <div className="col-4">
                            <label style={LABEL_STYLE}>PT Number</label>
                            <input
                                type="text"
                                name="ptNumber"
                                value={form.ptNumber}
                                onChange={handleChange}
                                placeholder="PT/25-26/0080"
                                style={FIELD_STYLE}
                            />
                        </div>
                    </div>
                </div>

                {/* Footer */}
                <div style={{
                    padding: "16px 28px",
                    borderTop: "1px solid #f1f5f9",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "flex-end",
                    gap: 10,
                }}>
                    <button
                        onClick={handleClose}
                        style={{
                            background: "#fff",
                            border: "1px solid #e2e8f0",
                            borderRadius: 8,
                            padding: "9px 22px",
                            fontSize: "0.95rem",
                            fontWeight: 600,
                            color: "#334155",
                            cursor: "pointer",
                        }}
                    >
                        Cancel
                    </button>
                    <button
                        onClick={handleSubmit}
                        disabled={isLoading || !form.date || !form.workDone || !form.atHours}
                        style={{
                            background: (isLoading || !form.date || !form.workDone || !form.atHours) ? "#94a3b8" : "#1e293b",
                            border: "none",
                            borderRadius: 8,
                            padding: "9px 22px",
                            fontSize: "0.95rem",
                            fontWeight: 600,
                            color: "#fff",
                            cursor: (isLoading || !form.date || !form.workDone || !form.atHours) ? "not-allowed" : "pointer",
                        }}
                    >
                        {isLoading ? "Saving…" : "Log service"}
                    </button>
                </div>
            </div>
        </Modal>
    );
}
